import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import mapping from 'atoms/Icon/mapping'
import Loading from 'atoms/Loading'
import {
  InstallationApplyRequest,
  InstallationDetail,
  InstallationErrorPayload,
  InstallationPlanChange,
} from 'common/types/kraken-core/Installation'
import { IntegrationModule } from 'common/types/kraken-core/IntegrationModule'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer'
import {
  applyInstallation,
  planInstallation,
} from 'services/kraken-core/installations/installations.service'
import InstallationErrorOutput from './InstallationErrorOutput'
import ParametersEditor from './ParametersEditor'
import { Accordion, Layout } from '@loadsmart/loadsmart-ui'

export interface IntegrationModuleEditWizardProps {
  installation: InstallationDetail
  integrationModule: IntegrationModule
  onClose: () => void
  onSuccess: (installation: InstallationDetail) => void
  onError: (error: string) => void
}

const STEPS = ['Version', 'Parameters', 'Plan', 'Finish'] as const

const VERSION_STEP = 0
const PARAMETERS_STEP = 1
const PLAN_STEP = 2
const FINISH_STEP = 3

/**
 * Recursively parses JSON strings into objects/arrays so nested payloads
 * (e.g. `settings`, `use_when`) render as structured JSON in the plan diff.
 */
export function deepParseJson(value: unknown): unknown {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (parsed !== null && typeof parsed === 'object') {
        return deepParseJson(parsed)
      }
    } catch {
      return value
    }
    return value
  }

  if (Array.isArray(value)) {
    return value.map(item => deepParseJson(item))
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
        key,
        deepParseJson(nested),
      ])
    )
  }

  return value
}

export function formatJsonForDiff(value: unknown): string {
  if (value === null || value === undefined) return ''
  const expanded = deepParseJson(value)
  if (typeof expanded === 'object' && expanded !== null) {
    return JSON.stringify(expanded, null, 2)
  }
  return String(expanded)
}

function buildApplyRequest(
  parameters: Record<string, unknown>,
  selectedVersion: string,
  pinnedVersion: string | null | undefined
): InstallationApplyRequest {
  const body: InstallationApplyRequest = { variables: parameters }
  if (selectedVersion && selectedVersion !== pinnedVersion) {
    body.module_version = selectedVersion
  }
  return body
}

/**
 * Drops parameters that the selected version's schema does not declare.
 *
 * Parameters are stored per installation while schemas belong to a module version, so a
 * version switch can leave behind keys that no longer exist. Anything the new schema adds is
 * filled in from its defaults by the form itself.
 */
function scopeParametersToSchema(
  parameters: Record<string, unknown> | null | undefined,
  schema: any
): Record<string, unknown> {
  const properties = schema?.properties
  if (!properties || typeof properties !== 'object') {
    return { ...(parameters || {}) }
  }

  return Object.entries(parameters || {}).reduce<Record<string, unknown>>(
    (scoped, [key, value]) => {
      if (key in properties) {
        scoped[key] = value
      }
      return scoped
    },
    {}
  )
}

function PlanChangeCard({ change }: { change: InstallationPlanChange }) {
  return (
    <Accordion>
      <Accordion.Toggle>
        <Layout.Group>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight="bold">
              {change.type}
            </Typography>
            {change.actions?.map(action => (
              <Chip key={action} size="small" label={action} />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {change.address}
          </Typography>
        </Layout.Group>
      </Accordion.Toggle>

      <Accordion.Body className="overflow-scroll">
        <ReactDiffViewer
          splitView
          hideLineNumbers
          oldValue={formatJsonForDiff(change.before)}
          newValue={formatJsonForDiff(change.after)}
          compareMethod={DiffMethod.TRIMMED_LINES}
          styles={{
            diffContainer: {
              width: '100%',
            },

            content: {
              width: '50%',
              maxWidth: '50%',
              minWidth: 0,
              overflow: 'hidden',
            },

            contentText: {
              display: 'block',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              fontSize: 12,
            },

            line: {
              fontSize: 12,
            },
          }}
        />
      </Accordion.Body>
    </Accordion>
  )
}

export default forwardRef<HTMLDivElement, IntegrationModuleEditWizardProps>(
  function IntegrationModuleEditWizard(
    { installation, integrationModule, onClose, onSuccess, onError: _onError },
    ref
  ) {
    const pinnedVersion = installation.module_version
    const latestVersion = integrationModule.current_version.version
    const versionOptions = integrationModule.versions

    const [activeStep, setActiveStep] = useState(VERSION_STEP)
    const [selectedVersion, setSelectedVersion] = useState(
      () => pinnedVersion || latestVersion || versionOptions[0]?.version || ''
    )
    const [parameters, setParameters] = useState<Record<string, unknown>>(
      () => (installation.parameters as Record<string, unknown>) || {}
    )
    const [isPlanning, setIsPlanning] = useState(false)
    const [isApplying, setIsApplying] = useState(false)
    const [planChanges, setPlanChanges] = useState<InstallationPlanChange[] | null>(null)
    const [planError, setPlanError] = useState<InstallationErrorPayload | null>(null)
    const [applyError, setApplyError] = useState<InstallationErrorPayload | null>(null)
    const [updatedInstallation, setUpdatedInstallation] = useState<InstallationDetail | null>(null)

    const isBusy = isPlanning || isApplying

    const selectedVersionData = useMemo(
      () => versionOptions.find(version => version.version === selectedVersion),
      [versionOptions, selectedVersion]
    )

    const selectedSchema = selectedVersionData?.input_schema
    const selectedUiSchema = selectedVersionData?.ui_schema

    const droppedParameters = useMemo(() => {
      const scoped = scopeParametersToSchema(installation.parameters, selectedSchema)
      return Object.keys((installation.parameters as Record<string, unknown>) || {}).filter(
        key => !(key in scoped)
      )
    }, [installation.parameters, selectedSchema])

    // Parameters belong to a version's schema, so re-derive them whenever the version changes.
    useEffect(() => {
      setParameters(scopeParametersToSchema(installation.parameters, selectedSchema))
    }, [selectedSchema, installation.parameters])

    const handleVersionChange = (event: SelectChangeEvent) => {
      setSelectedVersion(event.target.value)
    }

    const runPlan = async () => {
      setIsPlanning(true)
      setPlanError(null)
      setPlanChanges(null)

      try {
        const body = buildApplyRequest(parameters, selectedVersion, pinnedVersion)
        const { error, response } = await planInstallation(installation.id, body)

        if (error) {
          setPlanError(error)
          return
        }

        setPlanChanges(response.data.changes || [])
      } catch {
        setPlanError({ message: 'Failed to update installation' })
      } finally {
        setIsPlanning(false)
      }
    }

    const handleApply = async () => {
      setIsApplying(true)
      setApplyError(null)
      setUpdatedInstallation(null)
      setActiveStep(FINISH_STEP)

      try {
        const body = buildApplyRequest(parameters, selectedVersion, pinnedVersion)
        const { error, response } = await applyInstallation(installation.id, body)

        if (error) {
          setApplyError(error)
          return
        }

        setUpdatedInstallation(response.data)
      } catch {
        setApplyError({ message: 'Failed to update installation' })
      } finally {
        setIsApplying(false)
      }
    }

    const handleBack = () => {
      if (isBusy) return
      setActiveStep(prev => Math.max(prev - 1, VERSION_STEP))
    }

    const handlePrimaryAction = () => {
      if (activeStep === VERSION_STEP) {
        setActiveStep(PARAMETERS_STEP)
        return
      }
      if (activeStep === PARAMETERS_STEP) {
        setActiveStep(PLAN_STEP)
        return
      }
      if (activeStep === PLAN_STEP) {
        if (planError) {
          runPlan()
          return
        }
        handleApply()
        return
      }
      if (applyError) {
        onClose()
        return
      }
      if (updatedInstallation) {
        onSuccess(updatedInstallation)
      }
    }

    useEffect(() => {
      if (activeStep !== PLAN_STEP) return
      runPlan()
      // Intentionally re-run only when entering the plan step with current params/version.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep])

    const primaryLabel =
      activeStep === VERSION_STEP || activeStep === PARAMETERS_STEP
        ? 'Next'
        : activeStep === PLAN_STEP
        ? planError
          ? 'Retry plan'
          : isPlanning
          ? 'Planning…'
          : 'Apply'
        : applyError
        ? 'Close'
        : 'Done'

    const primaryDisabled =
      isBusy ||
      (activeStep === VERSION_STEP && !selectedVersion) ||
      (activeStep === PLAN_STEP && !planError && planChanges === null)

    const versionLabel = selectedVersion || '—'

    return (
      <Paper
        ref={ref}
        sx={{
          position: 'relative',
          minWidth: '80%',
          minHeight: '80%',
          maxWidth: '80%',
          maxHeight: '80%',
          overflow: 'hidden',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          flexDirection={'row'}
          display="flex"
          alignItems="center"
          sx={{
            p: 2,
          }}
        >
          <Avatar
            sizes="large"
            sx={{
              backgroundColor: 'secondary.main',
            }}
          >
            {mapping['IntegrationModule']}
          </Avatar>
          <Stack sx={{ pl: 2 }}>
            <Typography variant="caption" fontWeight={'bold'}>
              Edit installation
            </Typography>
            <Typography variant="h2" fontWeight={'bold'} sx={{ flexShrink: 0 }}>
              {integrationModule.name || 'Integration Module'}
            </Typography>
          </Stack>
        </Box>

        <Divider />

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{ width: '100%', pt: 2, flexShrink: 0 }}
        >
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Stack sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {activeStep === VERSION_STEP && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack spacing={2}>
                <Typography variant="h6">Module version</Typography>
                <Typography variant="body2" color="text.secondary">
                  Parameters on the next step come from the schema of the version you pick here.
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="module-version-label">Module version</InputLabel>
                  <Select
                    labelId="module-version-label"
                    label="Module version"
                    value={selectedVersion}
                    onChange={handleVersionChange}
                  >
                    {versionOptions.map(version => (
                      <MenuItem key={version.id} value={version.version}>
                        {`${version.version} - ${new Date(
                          version.created_at
                        ).toLocaleDateString()}`}
                        {version.version === pinnedVersion ? ' (current)' : ''}
                        {version.version === latestVersion && version.version !== pinnedVersion
                          ? ' (latest)'
                          : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {pinnedVersion
                      ? `This installation is on ${pinnedVersion}.`
                      : 'This installation is not pinned to a version yet.'}
                  </FormHelperText>
                </FormControl>
                {installation.is_outdated && latestVersion ? (
                  <Alert severity="info">
                    A newer version of this module is available ({latestVersion}).
                  </Alert>
                ) : null}
              </Stack>
            </Paper>
          )}

          {activeStep === PARAMETERS_STEP && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6">Parameters</Typography>
                  <Chip size="small" label={`version ${versionLabel}`} />
                </Stack>
                {droppedParameters.length > 0 ? (
                  <Alert severity="warning">
                    {`Version ${versionLabel} does not declare: ${droppedParameters.join(
                      ', '
                    )}. These will be removed when you apply.`}
                  </Alert>
                ) : null}
                <ParametersEditor
                  formKey={selectedVersion}
                  schema={selectedSchema}
                  uiSchema={selectedUiSchema}
                  data={parameters}
                  onChange={setParameters}
                />
              </Stack>
            </Paper>
          )}

          {activeStep === PLAN_STEP && (
            <Paper sx={{ p: 2, mb: 2 }}>
              {isPlanning && <Loading className="w-full flex justify-center items-center" />}
              {!isPlanning && planError && <InstallationErrorOutput error={planError} />}
              {!isPlanning && !planError && planChanges && (
                <Stack spacing={2}>
                  <Typography variant="h6">Proposed changes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Version: {versionLabel}
                  </Typography>
                  {planChanges.length === 0 ? (
                    <Alert severity="info">No changes detected for the selected parameters.</Alert>
                  ) : (
                    <Stack spacing={1}>
                      {planChanges.map(change => (
                        <PlanChangeCard
                          key={`${change.address}-${change.actions?.join('-')}`}
                          change={change}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}
            </Paper>
          )}

          {activeStep === FINISH_STEP && (
            <Paper sx={{ p: 2, mb: 2 }}>
              {isApplying && <Loading className="w-full flex justify-center items-center" />}
              {!isApplying && applyError && <InstallationErrorOutput error={applyError} />}
              {!isApplying && updatedInstallation && !applyError && (
                <Stack spacing={1}>
                  <Alert severity="success">Installation updated successfully.</Alert>
                  <Typography variant="body2">Installation ID: {updatedInstallation.id}</Typography>
                  {updatedInstallation.module_version ? (
                    <Typography variant="body2">
                      Module version: {updatedInstallation.module_version}
                    </Typography>
                  ) : null}
                </Stack>
              )}
            </Paper>
          )}
        </Stack>

        <Divider />
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'white',
            p: 2,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button
              variant="contained"
              disabled={
                activeStep === VERSION_STEP || (activeStep === FINISH_STEP && !applyError) || isBusy
              }
              onClick={handleBack}
            >
              Previous
            </Button>
            <Button variant="contained" disabled={primaryDisabled} onClick={handlePrimaryAction}>
              {isApplying ? 'Applying…' : primaryLabel}
            </Button>
          </Stack>
        </Box>
      </Paper>
    )
  }
)
